import type { RunDetailResponse } from "./workflows-api.ts";

export type WorkflowRunSnapshotKind = "overview" | "run";

export type WorkflowRunSnapshotMergeOptions = {
  preserveCurrentDetails?: boolean;
};

export function workflowRunDetailPath(runId: string, source: string): string {
  const params = new URLSearchParams({ source, view: "tree", includeCounts: "false" });
  return `/runs/${encodeURIComponent(runId)}?${params}`;
}

const mergeExactCount = <T>(
  current: T | null | undefined,
  incoming: T | null | undefined,
  preferIncoming: boolean,
): T | undefined => {
  const preferred = preferIncoming ? incoming : current;
  const fallback = preferIncoming ? current : incoming;
  return preferred !== null && preferred !== undefined ? preferred : fallback ?? undefined;
};

function definedProperties<T extends object>(value: T): Partial<T> {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as Partial<T>;
}

const TERMINAL_RUN_STATUSES = new Set(["completed", "failed", "timed_out", "cancelled"]);

function isTerminalRunStatus(status: string): boolean {
  return TERMINAL_RUN_STATUSES.has(status.toLowerCase());
}

function mergeOverviewRun(current: RunDetailResponse["run"], incoming: RunDetailResponse["run"]): RunDetailResponse["run"] {
  const merged = { ...current, ...definedProperties(incoming) };
  const currentStatusIsTerminal = isTerminalRunStatus(current.status);
  const incomingStatusIsNonTerminal = !isTerminalRunStatus(incoming.status);
  if (currentStatusIsTerminal && incomingStatusIsNonTerminal) {
    merged.status = current.status;
    merged.completedAt = current.completedAt;
    merged.error = current.error;
    merged.label = current.label;
  } else {
    const label = incoming.label ?? current.label;
    if (label !== undefined) merged.label = label;
  }
  return merged;
}

function enrichRunLabel(current: RunDetailResponse["run"], incoming: RunDetailResponse["run"]): RunDetailResponse["run"] {
  const label = incoming.label?.trim();
  return !current.label && label ? { ...current, label: incoming.label } : current;
}

/**
 * Overview snapshots are deliberately cheap and may arrive after an exact
 * full-run snapshot has started. Full-run snapshots therefore enrich the
 * current detail with exact counts and any computed retry progress label while
 * keeping the latest overview fields. An ordered overview may advance a run,
 * but a terminal run cannot regress to an older non-terminal status.
 */
export function mergeWorkflowRunSnapshot(
  current: RunDetailResponse | undefined,
  incoming: RunDetailResponse,
  kind: WorkflowRunSnapshotKind,
  options: WorkflowRunSnapshotMergeOptions = {},
): RunDetailResponse {
  if (!current) return incoming;

  const merged = kind === "overview" && !options.preserveCurrentDetails
    ? { ...current, ...definedProperties(incoming), run: mergeOverviewRun(current.run, incoming.run) }
    : { ...current };

  return {
    ...merged,
    ...(kind === "run" ? { run: enrichRunLabel(current.run, incoming.run) } : {}),
    stepStatusCounts: mergeExactCount(current.stepStatusCounts, incoming.stepStatusCounts, kind === "run"),
    durableWaitCounts: mergeExactCount(current.durableWaitCounts, incoming.durableWaitCounts, kind === "run"),
    diagnosticCounts: mergeExactCount(current.diagnosticCounts, incoming.diagnosticCounts, kind === "run"),
  };
}
